import React, { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Record } from 'immutable';

// Types
import { MeetingItem } from 'Shared/types/MeetingItem.type';
// import { WorkspaceComment } from 'Shared/types/WorkspaceComment.type';

// Selectors
import { getCurrentWorkspaceType } from 'Shared/selectors/common';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useApp } from 'Shared/utils/hooks/use-app';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Helpers
import MeetingItemHelpers from 'Shared/model_helpers/MeetingItem.helper';
// import WorkspaceItemCommentHelper from 'Shared/model_helpers/WorkspaceItemComment.helper';

// Utils
import { trackAddedComment } from 'Shared/tracking/tracking';
import { isEmpty } from 'Wpds/components/rich-text/utils/EditorState.utils';

// Actions
import * as workspaceItemCommentActions from 'Shared/actions/WorkspaceItemComments.actions';

// Components
import WpTextField from 'Wpds/components/text-field/WpTextField';
import WpAvatar from 'Wpds/components/avatar/WpAvatar';
import CommentTextEditor from 'Shared/components/Comments/CommentTextEditor/CommentTextEditor';

// Styles
import './MeetingItemCommentsInput.scss';

type Props = {
  meetingItem: Record<MeetingItem>;
  scrollToCallback?: Function;
  onChange?: Function;
  onCreateSucess?: Function;
  enterToSend?: boolean;
  meetingModeSessionId?: string;
  simpleInput?: boolean;
  showHint?: boolean;
  showSaveButton?: boolean;
  hideVideo?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  editorState?;
  // comment?: Record<WorkspaceComment>;
};

const MeetingItemCommentsInput = ({
  meetingItem,
  scrollToCallback,
  onChange,
  simpleInput = false,
  meetingModeSessionId,
  enterToSend,
  showHint,
  showSaveButton,
  hideVideo,
  autoFocus = true,
  placeholder,
  editorState,
  onCreateSucess,
}: // comment,
Props) => {
  const dispatch = useDispatch();
  const [isOpen, toggleIsOpen] = useToggle(false);
  const [isSaving, toggleIsSaving] = useToggle(false);
  const [isResettingForSafari, toggleIsResettingForSafari] = useToggle(false);
  const { tr } = useTranslation();

  const { appId, isZoom } = useApp();

  const meetingItemId = MeetingItemHelpers.getId(meetingItem);
  const workspaceId = MeetingItemHelpers.getWorkspaceId(meetingItem);
  const { user } = useCurrentUser();
  const workspaceType = useSelector(getCurrentWorkspaceType());

  // const initialValue = createRichTextEditorState(WorkspaceItemCommentHelper.getBody(comment));

  const handleCreateComment = useCallback(
    content => {
      toggleIsSaving();

      // if (comment) {
      //   const contentToSend = { ...comment.toJS(), ...content, slug: WorkspaceItemCommentHelper.getUuid(comment) };
      //   return dispatch(workspaceItemCommentActions.updateWorkspaceItemComment(contentToSend));
      // }

      let contentToSend = content;

      if (meetingModeSessionId) {
        contentToSend = { ...content, meetingModeSessionId };
      }
      return dispatch(workspaceItemCommentActions.createWorkspaceItemComment(meetingItemId, contentToSend)).then(() => {
        toggleIsOpen();
        if (scrollToCallback) scrollToCallback();
        if (onCreateSucess) onCreateSucess();
        toggleIsSaving();
        trackAddedComment(appId, meetingItem.toJS().itemType, workspaceType);
        if (simpleInput) {
          toggleIsResettingForSafari();
          setTimeout(() => {
            toggleIsResettingForSafari();
          }, 1);
        }
      });
    },
    [dispatch, workspaceItemCommentActions, meetingModeSessionId]
  );

  const handleOpenCommentInput = () => {
    toggleIsOpen();
    // need a little timeout to allow for the comment
    // input to open up
    if (scrollToCallback) {
      setTimeout(() => {
        scrollToCallback();
      }, 1);
    }
  };

  useEffect(() => {
    if (!isOpen && !isEmpty(editorState)) toggleIsOpen();
  }, [isOpen, editorState]);

  const baseClassName = 'MeetingItemCommentsInput';

  const Input = (
    <CommentTextEditor
      meetingItem={meetingItem.toJS()}
      handleCreateComment={handleCreateComment}
      toolbarPlacement="bottom"
      hideVideo={hideVideo || isZoom}
      enterToSend={enterToSend}
      showSaveButton={showSaveButton}
      showHint={showHint}
      outerEditorState={editorState}
      autoFocus={autoFocus}
      isSaving={isSaving}
      onChange={onChange}
      placeholder={placeholder}
      workspaceId={workspaceId}
    />
  );

  if (isResettingForSafari) return null;
  if (simpleInput) return <>{Input}</>;

  return (
    <div className={baseClassName}>
      {isSaving && (
        <div className={`${baseClassName}__saving`}>
          <div className={`${baseClassName}__saving__pill`}>
            <svg width={20} height={20} viewBox="0 0 50 50" className={`${baseClassName}__saving__pill__spinner`}>
              <path d="M43.935 25.145c0-10.318-8.364-18.683-18.683-18.683-10.318 0-18.683 8.365-18.683 18.683h4.068c0-8.071 6.543-14.615 14.615-14.615s14.615 6.543 14.615 14.615h4.068z">
                <animateTransform
                  attributeType="xml"
                  attributeName="transform"
                  type="rotate"
                  from="0 25 25"
                  to="360 25 25"
                  dur="0.6s"
                  repeatCount="indefinite"
                />
              </path>
            </svg>
            {tr('perform.meeting_item.comments_input.saving')}
          </div>
        </div>
      )}
      <div className={`${baseClassName}__avatar`}>
        <WpAvatar avatar={user.avatar} size="sm" />
      </div>
      <div className={`${baseClassName}__input`}>
        {!isOpen ? (
          <div
            className={`${baseClassName}__placeholder`}
            onClick={handleOpenCommentInput}
            data-test="MeetingItemCommentsInput-placeholder-wrapper"
          >
            <WpTextField
              id="enter-comment-slim"
              label={tr('perform.meeting_item.comments_input.add_comment_label')}
              labelHidden
              placeholder={placeholder || tr('perform.meeting_item.comments_input.leave_a_comment_placeholder')}
            />
          </div>
        ) : (
          <>{Input}</>
        )}
      </div>
    </div>
  );
};

export default MeetingItemCommentsInput;
